"use client";

import FilterButtons from "@/components/ui/buttons/FilterButtons";
import InputField from "@/components/ui/inputs/InputField";
import SelectInput from "@/components/ui/inputs/SelectInput";
import {
  getContinentCounts,
  getInitialContinents,
  isValidContinent,
} from "@/helpers/continents";
import { Continent } from "@/types/continent";
import type { Tour } from "@/types/tour";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";
import TourGridCard from "../cards/TourGridCard";

type ToursGridProps = {
  tours: Tour[];
};

type OrderBy =
  | "popular"
  | "price_asc"
  | "price_desc"
  | "duration_asc"
  | "duration_desc";

export default function ToursGrid({ tours }: ToursGridProps) {
  const searchParams = useSearchParams();
  const continentFromUrl = searchParams.get("continent") as Continent | null;

  const router = useRouter();
  const pathname = usePathname();

  const [query, setQuery] = useState("");
  const [orderBy, setOrderBy] = useState<OrderBy>("popular");
  const [continents, setContinents] = useState<Continent[]>(
    getInitialContinents(continentFromUrl),
  );

  const didCleanUrl = useRef(false);

  useEffect(() => {
    if (!continentFromUrl) return;
    if (didCleanUrl.current) return;

    didCleanUrl.current = true;

    // Remove only the continent param; keep other params intact.
    const params = new URLSearchParams(searchParams.toString());
    params.delete("continent");
    const qs = params.toString();

    router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
  }, [continentFromUrl, pathname, router, searchParams]);

  const continentCounts = useMemo(() => getContinentCounts(tours), [tours]);

  const normalizeText = (value: string) =>
    value
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "")
      .toLowerCase();

  const visibleTours = useMemo(() => {
    let result = tours;
    // SEARCH
    if (query.trim()) {
      const q = normalizeText(query);

      result = result.filter((tour) => {
        const itineraryTexts = Array.isArray(tour.itinerary)
          ? tour.itinerary.flatMap((item) => [
              item.title ?? "",
              item.description ?? "",
            ])
          : [];

        const searchFields = [
          tour.title ?? "",
          tour.long_title ?? "",
          tour.main_subtitle ?? "",
          tour.destination ?? "",
          ...itineraryTexts,
        ].map(normalizeText);

        return searchFields.some((field) => field.includes(q));
      });
    }

    if (continents.length > 0) {
      result = result.filter(
        (tour) =>
          isValidContinent(tour.continent) &&
          continents.includes(tour.continent),
      );
    }

    // ORDER
    const getPrice = (value: unknown) => {
      if (value === "" || value === null || value === undefined) return 0;
      const n = Number(value);
      return Number.isNaN(n) ? 0 : n;
    };

    result = [...result].sort((a, b) => {
      if (orderBy === "price_asc") {
        return getPrice(a.promo_price) - getPrice(b.promo_price);
      }

      if (orderBy === "price_desc") {
        return getPrice(b.promo_price) - getPrice(a.promo_price);
      }

      if (orderBy === "duration_asc") {
        return (
          (Number(a.promo_duration) || Infinity) -
          (Number(b.promo_duration) || Infinity)
        );
      }

      if (orderBy === "duration_desc") {
        return (
          (Number(b.promo_duration) || 0) - (Number(a.promo_duration) || 0)
        );
      }

      // popular = backend order
      return 0;
    });

    return result.filter((tour): tour is Tour => Boolean(tour?.url));
  }, [tours, query, orderBy, continents]);

  return (
    <>
      <div className='w-full max-w-300 bg-[#F5F5F5] rounded-xl pb-6 flex flex-col mt-6 mb-10 justify-center'>
        <div className='w-full flex mt-2 md:mt-5 md:mb-6 mb-4 justify-center'>
          <FilterButtons
            value={continents}
            onChange={(next) => {
              setContinents(next);
              if (next.length > 0) {
                setQuery("");
                setOrderBy("popular");
              }
            }}
            counts={continentCounts}
          />
        </div>
        <div className='w-full px-6 mb-4'>
          <div
            className='h-px w-full'
            style={{ height: "1px", backgroundColor: "black", opacity: "30%" }}
          />
        </div>
        {/* controls */}
        {continents.length > 0 ? (
          <div className='flex w-full mt-2 justify-center items-center'>
            <button
              onClick={() => setContinents([])}
              className='px-6 bg-white py-2 cursor-pointer rounded-full border border-black/20 text-sm font-medium hover:bg-black/5 transition-colors'
            >
              Zobraziť všetky kontitenty
            </button>
          </div>
        ) : (
          <div className='flex w-full justify-center items-center'>
            <div className='relative w-full max-w-4xl flex justify-center'>
              <div className='flex flex-col md:flex-row gap-1 md:gap-4 lg:gap-24 w-full justify-center items-center'>
                {/* SEARCH / CLEAR SWAP */}
                <div className='relative max-w-xs w-full'>
                  {/* SEARCH */}
                  <div
                    className={`transition-all duration-300 ease-out ${
                      continents.length > 0
                        ? "opacity-0 translate-y-2 pointer-events-none"
                        : "opacity-100 translate-y-0"
                    }`}
                  >
                    <InputField
                      name='search'
                      type='text'
                      placeholder='Hľadať výlet…'
                      value={query}
                      onChange={(e) => {
                        const value = e.target.value;
                        setQuery(value);

                        if (continents.length > 0 && value.trim().length > 0) {
                          setContinents([]);
                        }
                      }}
                    />
                  </div>

                  {/* CLEAR CONTINENTS */}
                  <div
                    className={`absolute inset-0 flex justify-center items-center transition-all duration-300 ease-out ${
                      continents.length > 0
                        ? "opacity-100 translate-y-0"
                        : "opacity-0 -translate-y-2 pointer-events-none"
                    }`}
                  >
                    <button
                      onClick={() => setContinents([])}
                      className='px-6 bg-white py-2 cursor-pointer rounded-full border border-black/20 text-sm font-medium hover:bg-black/5 transition-colors'
                    >
                      Zrušiť filtrovanie
                    </button>
                  </div>
                </div>

                {/* ORDER SELECT (ALWAYS VISIBLE) */}
                <div className='max-w-xs w-full'>
                  <SelectInput
                    name='orderBy'
                    value={orderBy}
                    onChange={(e) => setOrderBy(e.target.value as OrderBy)}
                    options={[
                      { value: "popular", label: "Najobľúbenejšie" },
                      { value: "price_desc", label: "Najdrahšie" },
                      { value: "price_asc", label: "Najlacnejšie" },
                      { value: "duration_desc", label: "Najdlhšie" },
                      { value: "duration_asc", label: "Najkratšie" },
                    ]}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* grid */}
      <div className='flex mb-10 md:mt-8 max-w-300 items-center justify-center flex-wrap gap-6 w-full'>
        {visibleTours.map((tour) => (
          <TourGridCard key={tour.url} tour={tour} />
        ))}
      </div>
    </>
  );
}
